import { useTranslation } from 'react-i18next';

import { Form, InputNumber } from '@arco-design/web-react';
import type { FormInstance } from '@arco-design/web-react/es/Form';

import { EnvVarsBlock } from '@/delivery-type-plugin/capabilities/env-vars';
import { maasInstanceReplicasRules } from '@/delivery-type-plugin/capabilities/maas-init/form-messages';
import { MaasGroupsConfigSection } from '@/delivery-type-plugin/capabilities/maas-node-groups/groups-config-section';
import {
  DEFAULT_MAAS_NODE_GROUP_FIELD_PATHS,
  type MaasNodeGroupFieldPaths,
} from '@/delivery-type-plugin/capabilities/maas-node-groups/nodeGroupFieldPaths';
import type { MaasNodeGroupsBlockOptions } from '@/delivery-type-plugin/capabilities/maas-node-groups/types';
import { MaasTaskTemplateBlock } from '@/delivery-type-plugin/capabilities/maas-task-template';
import { useResourceSectionReadOnly, useSceneLayoutContext } from '@/delivery-type-plugin/components';
import { isOrderPresetFieldLocked } from '@/delivery-type-plugin/platform/access-policy';

/** 按拓扑给新建节点默认名加前缀；普通推理无前缀，后续 worker 追加序号避免重名 */
function resolveMiNodeNamePrefix(paths: MaasNodeGroupFieldPaths): string {
  if (paths.runMode.startsWith('prefill')) return 'prefill';
  if (paths.runMode.startsWith('decode')) return 'decode';
  if (paths.runMode.startsWith('kvOffload')) return 'kv';
  return '';
}

function buildMiDefaultGroupName(prefix: string) {
  return (role: 'master' | 'worker', index: number) => {
    const base = prefix ? `${prefix}-${role}` : role;
    if (role === 'worker' && index > 0) return `${base}-${index}`;
    return base;
  };
}

export interface MiInferenceNodeGroupsBlockProps {
  form: FormInstance;
  options?: MaasNodeGroupsBlockOptions;
  /** 当前拓扑字段路径；默认普通推理节点 */
  fieldPaths?: MaasNodeGroupFieldPaths;
  /** 是否在节点配置末尾展示推理任务配置 */
  showTaskConfig?: boolean;
  /** 是否展示拓扑级副本数（缓存节点不支持集群级扩副本，传 false） */
  showClusterReplicas?: boolean;
}

/** 单套推理拓扑：副本数 → 环境变量 → 部署模式与节点 → 任务配置（初始化命令仅在节点抽屉内） */
export function MiInferenceNodeGroupsBlock({
  form,
  options,
  fieldPaths,
  showTaskConfig = false,
  showClusterReplicas = true,
}: MiInferenceNodeGroupsBlockProps) {
  const { t } = useTranslation();
  const sectionReadOnly = useResourceSectionReadOnly();
  const { resourceEditMode, presetConfigSnapshot } = useSceneLayoutContext();
  const { nodeGroupVariant: _variant, showInstanceReplicas: _replicas, ...sectionOptions } = options ?? {};
  const paths = fieldPaths ?? DEFAULT_MAAS_NODE_GROUP_FIELD_PATHS;
  const replicasLabel = t('data-center:maas.replicas');
  const scriptHint = options?.initCommandScriptHint;
  const getDefaultGroupName = buildMiDefaultGroupName(resolveMiNodeNamePrefix(paths));
  /** 优先已裁剪的 presetConfigSnapshot，避免未勾选模块残留 initValues 误锁副本 */
  const presetForReplicasLock = presetConfigSnapshot ?? (options?.initValues as Record<string, unknown> | undefined);
  const replicasLocked = sectionReadOnly || isOrderPresetFieldLocked(resourceEditMode, presetForReplicasLock, paths.replicas);

  return (
    <div>
      {showClusterReplicas ? (
        <Form.Item field={paths.replicas} label={replicasLabel} initialValue={1} rules={maasInstanceReplicasRules(replicasLabel, 1)}>
          <InputNumber min={1} precision={0} mode="button" disabled={replicasLocked} />
        </Form.Item>
      ) : (
        <Form.Item field={paths.replicas} hidden initialValue={1}>
          <InputNumber />
        </Form.Item>
      )}
      <div style={{ marginTop: 12 }}>
        <EnvVarsBlock
          form={form}
          paths={{ list: paths.commonEnv }}
          options={{ useCard: false }}
          blockMode="edit"
          title={t('data-center:maas.common.env.vars')}
        />
      </div>
      <div style={{ marginTop: 12 }}>
        <MaasGroupsConfigSection
          form={form}
          {...sectionOptions}
          showInstanceReplicas={false}
          fieldPaths={paths}
          getDefaultGroupName={getDefaultGroupName}
        />
      </div>
      {showTaskConfig ? (
        <div style={{ marginTop: 12 }}>
          <MaasTaskTemplateBlock
            form={form}
            paths={{ list: paths.taskTemplate }}
            blockMode="edit"
            options={{
              listField: paths.taskTemplate,
              masterNodesField: paths.masterNodes,
              workerNodesField: paths.workerNodes,
              scriptHint,
              sectionTitle: t('data-center:maas.inference.task.config'),
            }}
          />
        </div>
      ) : null}
    </div>
  );
}
